import {
  type ChangeEvent,
  type KeyboardEvent,
  type SyntheticEvent,
  useEffect,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  activeFormats,
  applyMarkdownFormat,
  type MarkdownAction,
  markdownShortcut,
} from '@/shared/lib/markdown-format';

import { MarkdownLinkPickerContext, type MarkdownLinkPickerProps } from './markdown-link-picker';
import styles from './MarkdownEditor.module.css';

/** A person offered after typing `@`. ``handle`` (unique) is what ends up in the text. */
export interface MentionOption {
  id: string;
  name: string;
  handle: string;
}

/** `@` at a word start, followed by the part typed so far (up to the caret). */
const MENTION_QUERY = /(^|\s)@([\p{L}\p{N}_.-]*)$/u;

interface MarkdownEditorProps {
  /** DOM id of the field (for an external `<label htmlFor>`). */
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** Accessible name of the textarea. */
  label: string;
  maxLength?: number;
  rows?: number;
  autoFocus?: boolean;
  /** Extra key handling (the multi-line editor only: single-line fields use shortcuts). */
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  /** A single line (`text` fields): an `<input>` with the inline tools only; no lists, headings
   *  or code blocks, and no line breaks. */
  inline?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  placeholder?: string;
  /** Typing `@` offers these people; picking one inserts `@handle` and calls `onMention`. */
  mentions?: readonly MentionOption[];
  onMention?: (option: MentionOption) => void;
}

type ToolKey =
  | 'bold'
  | 'italic'
  | 'strikethrough'
  | 'unorderedList'
  | 'orderedList'
  | 'checklist'
  | 'link'
  | 'quote'
  | 'code'
  | 'codeBlock'
  | 'rule';
interface Tool {
  action: MarkdownAction;
  icon: ReactNode;
  key: ToolKey;
}

const BUTTONS: Tool[] = [
  { action: 'bold', icon: <strong>B</strong>, key: 'bold' },
  { action: 'italic', icon: <em>I</em>, key: 'italic' },
  { action: 'strikethrough', icon: <s>S</s>, key: 'strikethrough' },
  { action: 'unorderedList', icon: '•≡', key: 'unorderedList' },
  { action: 'orderedList', icon: '1.', key: 'orderedList' },
  { action: 'checklist', icon: '☑', key: 'checklist' },
];
const BUTTONS_AFTER_HEADING: Tool[] = [
  { action: 'link', icon: '🔗', key: 'link' },
  { action: 'quote', icon: '❝', key: 'quote' },
  { action: 'code', icon: '</>', key: 'code' },
  { action: 'codeBlock', icon: '{ }', key: 'codeBlock' },
  { action: 'rule', icon: '―', key: 'rule' },
];

const INLINE_ACTIONS: readonly MarkdownAction[] = ['bold', 'italic', 'link', 'code'];

/**
 * The formatting buttons alone (also used floating above a grid cell). Presses never take the
 * focus from the field being edited, so its selection survives. `inline` offers bold, italic,
 * link and code; otherwise the full set including lists, headings, quotes and code blocks.
 */
export function MarkdownToolbar({
  inline = false,
  pressed,
  onAction,
}: {
  inline?: boolean;
  /** Actions that already apply to the selection (shown as pressed). */
  pressed?: ReadonlySet<MarkdownAction>;
  onAction: (action: MarkdownAction, level?: number) => void;
}) {
  const { t } = useTranslation();
  const button = ({ action, icon, key }: Tool) => (
    <button
      key={key}
      type="button"
      className={styles.tool}
      title={t(`markdownToolbar.${key}`)}
      aria-label={t(`markdownToolbar.${key}`)}
      {...(pressed && { 'aria-pressed': pressed.has(action) })}
      // Keep the textarea selection: a button press must not blur it.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={() => {
        onAction(action);
      }}
    >
      {icon}
    </button>
  );
  return (
    <div
      className={`${styles.toolbar} ${inline ? styles.toolbarInline : ''}`}
      role="toolbar"
      aria-label={t('markdownToolbar.label')}
    >
      {inline ? (
        [...BUTTONS, ...BUTTONS_AFTER_HEADING]
          .filter((tool) => INLINE_ACTIONS.includes(tool.action))
          .map(button)
      ) : (
        <>
          {BUTTONS.map(button)}
          <HeadingMenu
            onPick={(level) => {
              onAction('heading', level);
            }}
          />
          {BUTTONS_AFTER_HEADING.map(button)}
        </>
      )}
    </div>
  );
}

function LinkPickerHost({
  render,
  ...props
}: MarkdownLinkPickerProps & { render: (props: MarkdownLinkPickerProps) => ReactNode }) {
  return render(props);
}

/**
 * Markdown textarea with a compact formatting toolbar. The toolbar only edits
 * the text (wraps the selection or inserts a template); typing Markdown by hand
 * works as before. The selection is restored after every edit.
 */
export function MarkdownEditor({
  value,
  onChange,
  label,
  maxLength,
  rows = 5,
  autoFocus,
  onKeyDown,
  inline = false,
  disabled = false,
  invalid = false,
  describedBy,
  placeholder,
  id,
  mentions,
  onMention,
}: MarkdownEditorProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  const pendingSelection = useRef<[number, number] | null>(null);
  const listId = useId();
  const [caret, setCaret] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [active, setActive] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const renderLinkPicker = useContext(MarkdownLinkPickerContext);
  // The selection the link chooser will replace (the textarea loses its own while a dialog is open).
  const [linking, setLinking] = useState<{ start: number; end: number } | null>(null);

  // Suggestions: only while the caret sits right after an `@word` that was not dismissed.
  const match = mentions ? MENTION_QUERY.exec(value.slice(0, caret)) : null;
  const typed = match?.[2] ?? '';
  const queryStart = match ? caret - typed.length - 1 : -1;
  const needle = typed.toLocaleLowerCase();
  const suggestions =
    match && mentions && dismissedAt !== queryStart
      ? mentions
          .filter(
            (option) =>
              option.name.toLocaleLowerCase().includes(needle) ||
              option.handle.toLocaleLowerCase().includes(needle),
          )
          .slice(0, 8)
      : [];
  const current = Math.min(active, Math.max(suggestions.length - 1, 0));

  const pick = (option: MentionOption) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const insert = `@${option.handle} `;
    const next = value.slice(0, queryStart) + insert + value.slice(caret);
    if (maxLength !== undefined && next.length > maxLength) return;
    const position = queryStart + insert.length;
    pendingSelection.current = [position, position];
    setCaret(position);
    setSelectionEnd(position);
    onChange(next);
    onMention?.(option);
  };

  // Put the selection back once React has applied the new value.
  useLayoutEffect(() => {
    const selection = pendingSelection.current;
    const textarea = textareaRef.current;
    if (!selection || !textarea) return;
    pendingSelection.current = null;
    textarea.focus();
    textarea.setSelectionRange(selection[0], selection[1]);
  }, [value]);

  const insertLink = (text: string, url: string) => {
    if (!linking) return;
    const insert = `[${text.replace(/[[\]]/g, '')}](${url})`;
    const next = value.slice(0, linking.start) + insert + value.slice(linking.end);
    setLinking(null);
    if (maxLength !== undefined && next.length > maxLength) return;
    const position = linking.start + insert.length;
    pendingSelection.current = [position, position];
    setCaret(position);
    setSelectionEnd(position);
    onChange(next);
  };

  const apply = (action: MarkdownAction, level?: number) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    if (action === 'link' && renderLinkPicker) {
      setLinking({ start: textarea.selectionStart, end: textarea.selectionEnd });
      return;
    }
    const edit = applyMarkdownFormat(
      action,
      value,
      textarea.selectionStart,
      textarea.selectionEnd,
      {
        ...(level !== undefined && { level }),
        placeholders: {
          text: t('markdownToolbar.placeholderText'),
          code: t('markdownToolbar.placeholderCode'),
        },
      },
    );
    if (maxLength !== undefined && edit.value.length > maxLength) return;
    pendingSelection.current = [edit.selectionStart, edit.selectionEnd];
    if (edit.value === value) {
      textarea.setSelectionRange(edit.selectionStart, edit.selectionEnd);
      textarea.focus();
      pendingSelection.current = null;
    } else {
      onChange(edit.value);
    }
  };

  const field = {
    id,
    className: styles.textarea,
    'aria-label': label,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
    value,
    maxLength,
    autoFocus,
    disabled,
    placeholder,
    role: mentions && !inline ? 'combobox' : undefined,
    'aria-expanded': mentions && !inline ? suggestions.length > 0 : undefined,
    'aria-controls': suggestions.length > 0 ? listId : undefined,
    'aria-activedescendant': suggestions.length > 0 ? `${listId}-${current}` : undefined,
    'aria-autocomplete': mentions && !inline ? ('list' as const) : undefined,
    onChange: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCaret(event.target.selectionStart ?? 0);
      setSelectionEnd(event.target.selectionEnd ?? 0);
      setActive(0);
      onChange(inline ? event.target.value.replace(/\r?\n/g, ' ') : event.target.value);
    },
    onSelect: (event: SyntheticEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCaret(event.currentTarget.selectionStart ?? 0);
      setSelectionEnd(event.currentTarget.selectionEnd ?? 0);
    },
    onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const shortcut = markdownShortcut(event.nativeEvent);
      if (shortcut && (!inline || INLINE_ACTIONS.includes(shortcut))) {
        event.preventDefault();
        apply(shortcut);
        return;
      }
      if (suggestions.length > 0) {
        const option = suggestions[current];
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          setActive((current + step + suggestions.length) % suggestions.length);
          return;
        }
        if ((event.key === 'Enter' || event.key === 'Tab') && option) {
          event.preventDefault();
          pick(option);
          return;
        }
        if (event.key === 'Escape') {
          // Close the list only; Escape must not also close the dialog / cancel the editor.
          event.preventDefault();
          event.stopPropagation();
          setDismissedAt(queryStart);
          return;
        }
      }
      if (!inline) onKeyDown?.(event as KeyboardEvent<HTMLTextAreaElement>);
    },
  };

  return (
    <div className={styles.editor}>
      {renderLinkPicker && linking && (
        <LinkPickerHost
          render={renderLinkPicker}
          selectedText={value.slice(linking.start, linking.end)}
          onInsert={insertLink}
          onClose={() => {
            setLinking(null);
            textareaRef.current?.focus();
          }}
        />
      )}
      {!disabled && (
        <MarkdownToolbar
          inline={inline}
          pressed={activeFormats(value, caret, selectionEnd)}
          onAction={apply}
        />
      )}
      {inline ? (
        <input ref={textareaRef} {...field} />
      ) : (
        <textarea ref={textareaRef} rows={rows} {...field} />
      )}
      {suggestions.length > 0 && (
        <ul
          id={listId}
          className={styles.mentions}
          role="listbox"
          aria-label={t('markdownToolbar.mentions')}
        >
          {suggestions.map((option, index) => (
            <li
              key={option.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === current}
              className={`${styles.mention} ${index === current ? styles.mentionActive : ''}`}
              // Keep the textarea focused and its caret in place.
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                pick(option);
              }}
            >
              <span>{option.name}</span>
              <span className={styles.mentionHandle}>@{option.handle}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HeadingMenu({ onPick }: { onPick: (level: number) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={styles.headingRoot}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          // Close the menu only; Escape must not also close the dialog / cancel the editor.
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={styles.tool}
        title={t('markdownToolbar.heading')}
        aria-label={t('markdownToolbar.heading')}
        aria-haspopup="true"
        aria-expanded={open}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        H ▾
      </button>
      {open && (
        <div className={styles.headingMenu} role="group" aria-label={t('markdownToolbar.heading')}>
          {[1, 2, 3, 4, 5, 6].map((level) => (
            <button
              key={level}
              type="button"
              className={styles.headingItem}
              aria-label={t('markdownToolbar.headingLevel', { level })}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                setOpen(false);
                onPick(level);
              }}
            >
              H{level}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
